import React, { useState } from 'react';
import './QuestionTemplate.css';

const QuestionTemplate = ({ onSubmit, onCancel, question = null, sectionId }) => {
  const [formData, setFormData] = useState({
    title: question?.title || '',
    description: question?.description || '',
    problemStatement: question?.problemStatement || '',
    videoLink: question?.videoLink || '',
    solution: question?.solution || '',
    solutionCode: question?.solutionCode || '',
    initialCode: question?.initialCode || '', // Deprecated - kept for backward compatibility
    sourceLanguage: question?.sourceLanguage || 'java',
    initialCodeJava: question?.initialCodeJava || '',
    initialCodePython: question?.initialCodePython || '',
    initialCodeJavaScript: question?.initialCodeJavaScript || '',
    initialCodeCpp: question?.initialCodeCpp || '',
    initialCodeC: question?.initialCodeC || '',
    solutionCodeJava: question?.solutionCodeJava || '',
    solutionCodePython: question?.solutionCodePython || '',
    solutionCodeJavaScript: question?.solutionCodeJavaScript || '',
    solutionCodeCpp: question?.solutionCodeCpp || '',
    solutionCodeC: question?.solutionCodeC || '',
    testCases: question?.testCases || '',
    hiddenTestCases: question?.hiddenTestCases || '',
    expectedOutput: question?.expectedOutput || '',
    constraints: question?.constraints || '',
    examples: question?.examples || '',
    followUp: question?.followUp || '',
    relatedTopics: question?.relatedTopics || '',
    acceptanceRate: question?.acceptanceRate || 0.0,
    likesCount: question?.likesCount || 0,
    dislikesCount: question?.dislikesCount || 0,
    submissionsCount: question?.submissionsCount || 0,
    successRate: question?.successRate || 0.0,
    isPremium: question?.isPremium || false,
    problemNumber: question?.problemNumber || 1,
    categoryTags: question?.categoryTags || '',
    timeLimitSeconds: question?.timeLimitSeconds || 1000,
    memoryLimitMB: question?.memoryLimitMB || 256,
    difficultyLevel: question?.difficultyLevel || 'EASY',
    questionOrder: question?.questionOrder || 1
  });

  const [activeTab, setActiveTab] = useState('problem');
  const [languageTab, setLanguageTab] = useState('java'); // For initial code tabs
  const [solutionTab, setSolutionTab] = useState('java'); // For solution code tabs

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const questionData = {
      ...formData,
      questionSection: sectionId ? { id: sectionId } : null
    };
    onSubmit(questionData);
  };

  const renderProblemTab = () => (
    <div className="tab-content">
      <div className="form-section">
        <h3>Basic Information</h3>
        <div className="form-row">
          <div className="form-group">
            <label>Problem Title *</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="e.g., Add Two Numbers"
              required
            />
          </div>
          <div className="form-group">
            <label>Problem Number</label>
            <input
              type="number"
              name="problemNumber"
              value={formData.problemNumber}
              onChange={handleInputChange}
              placeholder="1"
            />
          </div>
        </div>

        <div className="form-group">
          <label>Description *</label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            placeholder="Brief description of the problem"
            rows="3"
            required
          />
        </div>

        <div className="form-group">
          <label>Problem Statement *</label>
          <textarea
            name="problemStatement"
            value={formData.problemStatement}
            onChange={handleInputChange}
            placeholder="Detailed problem statement with examples..."
            rows="8"
            required
          />
        </div>
      </div>

      <div className="form-section">
        <h3>Examples & Constraints</h3>
        <div className="form-group">
          <label>Examples</label>
          <textarea
            name="examples"
            value={formData.examples}
            onChange={handleInputChange}
            placeholder="Input: l1 = [2,4,3], l2 = [5,6,4]&#10;Output: [7,0,8]&#10;Explanation: 342 + 465 = 807"
            rows="6"
          />
        </div>

        <div className="form-group">
          <label>Constraints</label>
          <textarea
            name="constraints"
            value={formData.constraints}
            onChange={handleInputChange}
            placeholder="• The number of nodes in each linked list is in the range [1, 100]&#10;• 0 <= Node.val <= 9&#10;• It is guaranteed that the list represents a number that does not have leading zeros."
            rows="4"
          />
        </div>
      </div>

      <div className="form-section">
        <h3>Video & Follow-up</h3>
        <div className="form-group">
          <label>Video Tutorial Link</label>
          <input
            type="url"
            name="videoLink"
            value={formData.videoLink}
            onChange={handleInputChange}
            placeholder="https://youtube.com/watch?v=..."
          />
        </div>

        <div className="form-group">
          <label>Follow-up Questions</label>
          <textarea
            name="followUp"
            value={formData.followUp}
            onChange={handleInputChange}
            placeholder="What if the digits are stored in forward order?&#10;What if you cannot modify the input lists?"
            rows="3"
          />
        </div>
      </div>
    </div>
  );

  const renderSolutionTab = () => (
    <div className="tab-content">
      <div className="form-section">
        <h3>Solution Details</h3>
        <div className="form-group">
          <label>Solution Explanation</label>
          <textarea
            name="solution"
            value={formData.solution}
            onChange={handleInputChange}
            placeholder="Detailed explanation of the solution approach..."
            rows="8"
          />
        </div>

        <div className="form-group">
          <label>Solution Code Templates (All Languages) *</label>
          <small className="form-help">
            ⚠️ <strong>Important:</strong> Provide complete working solution code for each language. This code will be shown to students when they view the solution.
          </small>
          
          <div className="language-tabs-container">
            <div className="language-tabs">
              <button type="button" onClick={() => setSolutionTab('java')} className={`language-tab-btn ${solutionTab === 'java' ? 'active-solution' : ''}`}>Java</button>
              <button type="button" onClick={() => setSolutionTab('python')} className={`language-tab-btn ${solutionTab === 'python' ? 'active-solution' : ''}`}>Python</button>
              <button type="button" onClick={() => setSolutionTab('javascript')} className={`language-tab-btn ${solutionTab === 'javascript' ? 'active-solution' : ''}`}>JavaScript</button>
              <button type="button" onClick={() => setSolutionTab('cpp')} className={`language-tab-btn ${solutionTab === 'cpp' ? 'active-solution' : ''}`}>C++</button>
              <button type="button" onClick={() => setSolutionTab('c')} className={`language-tab-btn ${solutionTab === 'c' ? 'active-solution' : ''}`}>C</button>
            </div>
            
            {solutionTab === 'java' && (
              <textarea
                name="solutionCodeJava"
                value={formData.solutionCodeJava}
                onChange={handleInputChange}
                placeholder="import java.util.*;&#10;&#10;public class Solution {&#10;    public int[] twoSum(int[] nums, int target) {&#10;        HashMap&lt;Integer, Integer&gt; map = new HashMap&lt;&gt;();&#10;        for (int i = 0; i &lt; nums.length; i++) {&#10;            int complement = target - nums[i];&#10;            if (map.containsKey(complement)) {&#10;                return new int[]{map.get(complement), i};&#10;            }&#10;            map.put(nums[i], i);&#10;        }&#10;        return new int[]{-1, -1};&#10;    }&#10;}"
                rows="15"
                className="code-editor"
              />
            )}
            
            {solutionTab === 'python' && (
              <textarea
                name="solutionCodePython"
                value={formData.solutionCodePython}
                onChange={handleInputChange}
                placeholder="class Solution:&#10;    def twoSum(self, nums, target):&#10;        seen = {}&#10;        for i, num in enumerate(nums):&#10;            complement = target - num&#10;            if complement in seen:&#10;                return [seen[complement], i]&#10;            seen[num] = i&#10;        return [-1, -1]"
                rows="15"
                className="code-editor"
              />
            )}
            
            {solutionTab === 'javascript' && (
              <textarea
                name="solutionCodeJavaScript"
                value={formData.solutionCodeJavaScript}
                onChange={handleInputChange}
                placeholder="class Solution {&#10;    twoSum(nums, target) {&#10;        const map = new Map();&#10;        for (let i = 0; i &lt; nums.length; i++) {&#10;            const complement = target - nums[i];&#10;            if (map.has(complement)) {&#10;                return [map.get(complement), i];&#10;            }&#10;            map.set(nums[i], i);&#10;        }&#10;        return [-1, -1];&#10;    }&#10;}"
                rows="15"
                className="code-editor"
              />
            )}
            
            {solutionTab === 'cpp' && (
              <textarea
                name="solutionCodeCpp"
                value={formData.solutionCodeCpp}
                onChange={handleInputChange}
                placeholder="#include &lt;vector&gt;&#10;#include &lt;unordered_map&gt;&#10;using namespace std;&#10;&#10;class Solution {&#10;public:&#10;    vector&lt;int&gt; twoSum(vector&lt;int&gt;&amp; nums, int target) {&#10;        unordered_map&lt;int, int&gt; map;&#10;        for (int i = 0; i &lt; nums.size(); i++) {&#10;            int complement = target - nums[i];&#10;            if (map.find(complement) != map.end()) {&#10;                return {map[complement], i};&#10;            }&#10;            map[nums[i]] = i;&#10;        }&#10;        return {-1, -1};&#10;    }&#10;};"
                rows="15"
                className="code-editor"
              />
            )}
            
            {solutionTab === 'c' && (
              <textarea
                name="solutionCodeC"
                value={formData.solutionCodeC}
                onChange={handleInputChange}
                placeholder="#include &lt;stdlib.h&gt;&#10;&#10;int* twoSum(int* nums, int numsSize, int target, int* returnSize) {&#10;    *returnSize = 2;&#10;    int* result = (int*)malloc(2 * sizeof(int));&#10;    for (int i = 0; i &lt; numsSize; i++) {&#10;        for (int j = i + 1; j &lt; numsSize; j++) {&#10;            if (nums[i] + nums[j] == target) {&#10;                result[0] = i;&#10;                result[1] = j;&#10;                return result;&#10;            }&#10;        }&#10;    }&#10;    result[0] = -1;&#10;    result[1] = -1;&#10;    return result;&#10;}"
                rows="15"
                className="code-editor"
              />
            )}
          </div>
        </div>

        <div className="form-group">
          <label>Initial Code Templates *</label>
          <small className="form-help">
            ⚠️ <strong>Important:</strong> Provide complete code templates for each language including imports, class definitions, solution method, test runner, and stdin handling. Do NOT leave any template empty - students need working code for all languages!
          </small>
          
          <div className="language-tabs-container">
            <div className="language-tabs">
              <button type="button" onClick={() => setLanguageTab('java')} className={`language-tab-btn ${languageTab === 'java' ? 'active-java' : ''}`}>Java</button>
              <button type="button" onClick={() => setLanguageTab('python')} className={`language-tab-btn ${languageTab === 'python' ? 'active-java' : ''}`}>Python</button>
              <button type="button" onClick={() => setLanguageTab('javascript')} className={`language-tab-btn ${languageTab === 'javascript' ? 'active-java' : ''}`}>JavaScript</button>
              <button type="button" onClick={() => setLanguageTab('cpp')} className={`language-tab-btn ${languageTab === 'cpp' ? 'active-java' : ''}`}>C++</button>
              <button type="button" onClick={() => setLanguageTab('c')} className={`language-tab-btn ${languageTab === 'c' ? 'active-java' : ''}`}>C</button>
            </div>
            
            {languageTab === 'java' && (
              <textarea
                name="initialCodeJava"
                value={formData.initialCodeJava}
                onChange={handleInputChange}
                placeholder="import java.util.*;&#10;&#10;public class Solution {&#10;    public boolean isValid(String s) {&#10;        // Write your code here&#10;        return false;&#10;    }&#10;    &#10;    public static void main(String[] args) {&#10;        Scanner scanner = new Scanner(System.in);&#10;        String input = scanner.nextLine().trim();&#10;        Solution solution = new Solution();&#10;        System.out.println(solution.isValid(input));&#10;        scanner.close();&#10;    }&#10;}"
                rows="15"
                className="code-editor"
              />
            )}
            
            {languageTab === 'python' && (
              <textarea
                name="initialCodePython"
                value={formData.initialCodePython}
                onChange={handleInputChange}
                placeholder="class Solution:&#10;    def is_valid(self, s):&#10;        # Write your code here&#10;        return False&#10;&#10;if __name__ == &quot;__main__&quot;:&#10;    import sys&#10;    input_data = sys.stdin.readline().strip()&#10;    solution = Solution()&#10;    print(solution.is_valid(input_data))"
                rows="15"
                className="code-editor"
              />
            )}
            
            {languageTab === 'javascript' && (
              <textarea
                name="initialCodeJavaScript"
                value={formData.initialCodeJavaScript}
                onChange={handleInputChange}
                placeholder="class Solution {&#10;    isValid(s) {&#10;        // Write your code here&#10;        return false;&#10;    }&#10;}&#10;&#10;const readline = require('readline');&#10;const rl = readline.createInterface({&#10;    input: process.stdin,&#10;    output: process.stdout&#10;});&#10;&#10;rl.on('line', (input) => {&#10;    const solution = new Solution();&#10;    console.log(solution.isValid(input.trim()));&#10;    rl.close();&#10;});"
                rows="15"
                className="code-editor"
              />
            )}
            
            {languageTab === 'cpp' && (
              <textarea
                name="initialCodeCpp"
                value={formData.initialCodeCpp}
                onChange={handleInputChange}
                placeholder="#include &lt;iostream&gt;&#10;#include &lt;string&gt;&#10;using namespace std;&#10;&#10;class Solution {&#10;public:&#10;    bool isValid(string s) {&#10;        // Write your code here&#10;        return false;&#10;    }&#10;};&#10;&#10;int main() {&#10;    string input;&#10;    getline(cin, input);&#10;    Solution solution;&#10;    cout &lt;&lt; (solution.isValid(input) ? &quot;true&quot; : &quot;false&quot;) &lt;&lt; endl;&#10;    return 0;&#10;}"
                rows="15"
                className="code-editor"
              />
            )}
            
            {languageTab === 'c' && (
              <textarea
                name="initialCodeC"
                value={formData.initialCodeC}
                onChange={handleInputChange}
                placeholder="#include &lt;stdio.h&gt;&#10;#include &lt;string.h&gt;&#10;#include &lt;stdbool.h&gt;&#10;&#10;bool isValid(char* s) {&#10;    // Write your code here&#10;    return false;&#10;}&#10;&#10;int main() {&#10;    char input[10000];&#10;    fgets(input, sizeof(input), stdin);&#10;    input[strcspn(input, &quot;\\n&quot;)] = 0;&#10;    printf(&quot;%s\\n&quot;, isValid(input) ? &quot;true&quot; : &quot;false&quot;);&#10;    return 0;&#10;}"
                rows="15"
                className="code-editor"
              />
            )}
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>Test Cases</h3>
        <div className="form-group">
          <label>Basic Test Cases (Visible - Run Code) *</label>
          <small>Format: input1, input2 | expected_output (one per line)</small>
          <textarea
            name="testCases"
            value={formData.testCases}
            onChange={handleInputChange}
            placeholder="anagram, nagaram | true&#10;rat, car | false&#10;listen, silent | true"
            rows="6"
            required
          />
          <small>These test cases are shown when users click "Run Code" (3-5 cases recommended)</small>
        </div>

        <div className="form-group">
          <label>Hidden Test Cases (Submit Only) *</label>
          <small>Format: input1, input2 | expected_output (one per line)</small>
          <textarea
            name="hiddenTestCases"
            value={formData.hiddenTestCases}
            onChange={handleInputChange}
            placeholder="anagram, nagaram | true&#10;rat, car | false&#10;a, a | true&#10;ab, ba | true&#10;... (add 15-20 cases including edge cases)"
            rows="10"
            required
          />
          <small>These test cases run when users click "Submit" (15-20 cases recommended, including edge cases)</small>
        </div>

        <div className="form-group">
          <label>Expected Output Format</label>
          <input
            type="text"
            name="expectedOutput"
            value={formData.expectedOutput}
            onChange={handleInputChange}
            placeholder="e.g., [7,0,8] or true or 42"
          />
        </div>
      </div>
    </div>
  );

  const renderSettingsTab = () => (
    <div className="tab-content">
      <div className="form-section">
        <h3>Problem Settings</h3>
        <div className="form-row">
          <div className="form-group">
            <label>Difficulty Level</label>
            <select
              name="difficultyLevel"
              value={formData.difficultyLevel}
              onChange={handleInputChange}
            >
              <option value="EASY">Easy</option>
              <option value="MEDIUM">Medium</option>
              <option value="HARD">Hard</option>
              <option value="EXPERT">Expert</option>
            </select>
          </div>
          <div className="form-group">
            <label>Question Order</label>
            <input
              type="number"
              name="questionOrder"
              value={formData.questionOrder}
              onChange={handleInputChange}
              placeholder="1"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Time Limit (seconds)</label>
            <input
              type="number"
              name="timeLimitSeconds"
              value={formData.timeLimitSeconds}
              onChange={handleInputChange}
              placeholder="1000"
            />
          </div>
          <div className="form-group">
            <label>Memory Limit (MB)</label>
            <input
              type="number"
              name="memoryLimitMB"
              value={formData.memoryLimitMB}
              onChange={handleInputChange}
              placeholder="256"
            />
          </div>
        </div>

        <div className="form-group">
          <label>Category Tags</label>
          <input
            type="text"
            name="categoryTags"
            value={formData.categoryTags}
            onChange={handleInputChange}
            placeholder="Linked List, Math, Recursion"
          />
        </div>

        <div className="form-group">
          <label>Related Topics</label>
          <input
            type="text"
            name="relatedTopics"
            value={formData.relatedTopics}
            onChange={handleInputChange}
            placeholder="Two Pointers, Stack, Queue"
          />
        </div>
      </div>

      <div className="form-section">
        <h3>Statistics & Premium</h3>
        <div className="form-row">
          <div className="form-group">
            <label>Acceptance Rate (%)</label>
            <input
              type="number"
              name="acceptanceRate"
              value={formData.acceptanceRate}
              onChange={handleInputChange}
              step="0.1"
              placeholder="45.2"
            />
          </div>
          <div className="form-group">
            <label>Success Rate (%)</label>
            <input
              type="number"
              name="successRate"
              value={formData.successRate}
              onChange={handleInputChange}
              step="0.1"
              placeholder="78.5"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Likes Count</label>
            <input
              type="number"
              name="likesCount"
              value={formData.likesCount}
              onChange={handleInputChange}
              placeholder="0"
            />
          </div>
          <div className="form-group">
            <label>Dislikes Count</label>
            <input
              type="number"
              name="dislikesCount"
              value={formData.dislikesCount}
              onChange={handleInputChange}
              placeholder="0"
            />
          </div>
        </div>

        <div className="form-group">
          <label>Submissions Count</label>
          <input
            type="number"
            name="submissionsCount"
            value={formData.submissionsCount}
            onChange={handleInputChange}
            placeholder="0"
          />
        </div>

        <div className="form-group checkbox-group">
          <label>
            <input
              type="checkbox"
              name="isPremium"
              checked={formData.isPremium}
              onChange={handleInputChange}
            />
            Premium Question
          </label>
        </div>
      </div>
    </div>
  );

  return (
    <div className="question-template">
      <div className="template-header">
        <h2>{question ? 'Edit Question' : 'Create New Question'}</h2>
        <p>Create a LeetCode-style programming problem</p>
      </div>

      <div className="template-tabs">
        <button
          className={`tab-button ${activeTab === 'problem' ? 'active' : ''}`}
          onClick={() => setActiveTab('problem')}
        >
          Problem
        </button>
        <button
          className={`tab-button ${activeTab === 'solution' ? 'active' : ''}`}
          onClick={() => setActiveTab('solution')}
        >
          Solution
        </button>
        <button
          className={`tab-button ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          Settings
        </button>
      </div>

      <form onSubmit={handleSubmit} className="template-form">
        {activeTab === 'problem' && renderProblemTab()}
        {activeTab === 'solution' && renderSolutionTab()}
        {activeTab === 'settings' && renderSettingsTab()}

        <div className="form-actions">
          <button type="button" onClick={onCancel} className="btn-secondary">
            Cancel
          </button>
          <button type="submit" className="btn-primary">
            {question ? 'Update Question' : 'Create Question'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default QuestionTemplate; 
